import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";

const CONTROLS = 'input,button,select,textarea,[role="slider"],[role="switch"],[role="checkbox"],[role="combobox"],[role="radio"]';
const ATTRS = ["placeholder", "title", "aria-label", "data-search", "alt"];
const SKIP = new Set(["SCRIPT", "STYLE", "TEMPLATE", "NOSCRIPT"]);

// Bottom-up searchable text for every element (own text + labels/placeholders/titles of descendants).
function collect(el, map) {
  let t = ATTRS.map((a) => el.getAttribute(a) || "").join(" ");
  if (el.tagName === "INPUT" && el.type === "text" && el.value) t += " " + el.value;
  for (const n of el.childNodes) {
    if (n.nodeType === 3) t += " " + n.nodeValue;
    else if (n.nodeType === 1 && !SKIP.has(n.tagName) && !n.hasAttribute("data-ws-keep")) t += " " + collect(n, map);
  }
  t = norm(t);
  map.set(el, t);
  return t;
}

export const norm = (t) => String(t).toLowerCase().replace(/colour/g, "color").replace(/grey/g, "gray").replace(/\s+/g, " ");
// Esc inside the search clears it instead of closing the Radix dialog (Radix listens in capture phase).
export const wsEscGuard = (orig) => (e) => {
  if (e.target?.closest?.('[data-testid="window-search"]')) { e.preventDefault(); return; }
  orig?.(e);
};
// Always visible while searching: titles, close/save/cancel actions and explicitly pinned areas.
const PIN = "[data-ws-pin],[data-ws-autopin]";
const ACTION = /^(save|save changes|cancel|close|apply|apply & preview|done|ok|confirm|update|create|submit|back|×|x)$/i;
function autoPin(scope) {
  scope.querySelectorAll("h1,h2,h3,[role=heading]").forEach((n) => n.setAttribute("data-ws-autopin", ""));
  scope.querySelectorAll("button").forEach((b) => {
    const label = (b.textContent || "").trim();
    const aria = `${b.getAttribute("aria-label") || ""} ${b.getAttribute("title") || ""}`.toLowerCase();
    const iconX = !label && b.querySelector("svg.lucide-x");
    if (ACTION.test(label) || /\bclose\b/.test(aria) || /\bclose\b/i.test(label) || iconX) b.setAttribute("data-ws-autopin", "");
  });
}
const READOUT = /^(#[0-9a-f]{3,8}|[\d\s.,%°x:+\-]*)$/;
const hasAll = (t, terms) => terms.every((q) => t.includes(q));

function clear(scope) {
  scope.removeAttribute("data-ws-active");
  scope.removeAttribute("data-ws-empty");
  scope.style.minHeight = "";
  scope.querySelectorAll("[data-ws-hide],[data-ws-hit],[data-ws-autopin]").forEach((n) => { n.removeAttribute("data-ws-hide"); n.removeAttribute("data-ws-hit"); n.removeAttribute("data-ws-autopin"); });
}

// Hides everything in `scope` that doesn't match; keeps whole rows/sections when their label matches.
function apply(scope, terms) {
  const h = scope.hasAttribute("data-ws-active") ? scope.style.minHeight : `${scope.offsetHeight}px`;
  clear(scope);
  if (!terms.length) return -1;
  scope.setAttribute("data-ws-active", "");
  scope.style.minHeight = h; // no jumping/re-centring while typing
  autoPin(scope);
  const map = new Map();
  collect(scope, map);
  let hits = 0;
  // ctx = titles of enclosing sections, so "background color" finds the Color row inside the Background section.
  const visit = (el, ctx) => {
    if (el.matches(PIN)) return false;
    const own = map.get(el) || "";
    const t = own + " " + ctx;
    const kids = [...el.children].filter((k) => map.has(k));
    const keeps = el.querySelector("[data-ws-keep]," + PIN);
    if (!keeps && !hasAll(t, terms)) { el.setAttribute("data-ws-hide", ""); return false; }
    const head = kids[0];
    // A title: short text, at most one toggle (switch/checkbox, never a field), and not just the first item of a uniform list.
    const headCtl = head ? head.querySelectorAll(CONTROLS).length : 0;
    const headToggleOnly = headCtl === 0 || (headCtl === 1 && !!head.querySelector('[role="switch"],button[role="switch"]'));
    const uniform = head && kids.length > 1 && kids[1].tagName === head.tagName && kids[1].className === head.className;
    const headTxt = head && kids.length > 1 && !uniform && headToggleOnly && !head.matches(CONTROLS) && (map.get(head) || "").length <= 48 ? map.get(head) : "";
    // If some child matches on its own text, siblings must match on their own too (titles don't drag whole sections in).
    const ownKid = kids.some((k) => hasAll(map.get(k) || "", terms));
    const kidCtx = ownKid ? "" : (deep ? ctx + " " + headTxt : (headTxt || ctx));
    if (!keeps) {
      const ctl = el.querySelectorAll(CONTROLS).length + (el.matches(CONTROLS) ? 1 : 0);
      const deeper = kids.slice(1).some((k) => hasAll((map.get(k) || "") + " " + kidCtx, terms));
      const headMatch = headTxt && !deeper && hasAll(headTxt + " " + ctx, terms);
      if (!kids.length || (ctl <= 2 && own.length <= 200) || headMatch || (!ownKid && hasAll(ctx, terms))) {
        if (own.length <= 120) el.setAttribute("data-ws-hit", "");
        hits += 1; return true;
      }
    }
    const res = kids.map((k) => visit(k, kidCtx));
    // Keep the section title and value readouts / swatches (no real words) next to a match.
    if (res.some(Boolean)) kids.forEach((k, i) => { if (!res[i] && ((k === head && headTxt) || READOUT.test((map.get(k) || "").trim()))) k.removeAttribute("data-ws-hide"); });
    return res.some(Boolean);
  };
  // Pass 1: only the nearest section title counts as context; pass 2 (if nothing found): all enclosing titles.
  let deep = false;
  const top = [...scope.children].filter((k) => map.has(k));
  top.forEach((k) => visit(k, ""));
  if (!hits) {
    scope.querySelectorAll("[data-ws-hide],[data-ws-hit]").forEach((n) => { n.removeAttribute("data-ws-hide"); n.removeAttribute("data-ws-hit"); });
    hits = 0;
    deep = true;
    top.forEach((k) => visit(k, ""));
  }
  if (!hits) scope.setAttribute("data-ws-empty", "");
  return hits;
}

// Tabbed panels marked data-ws-panel stay mounted (hidden) so search can reach inactive tabs too.
// Drop inside any window: filters the window's content in place as the user types.
export default function WindowSearch({ scope, inline = false, minControls = 6, className = "" }) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState(-1);
  const [enough, setEnough] = useState(false);
  const qRef = useRef("");
  qRef.current = q;

  const scopeEl = () => (scope?.current) || rootRef.current?.parentElement;
  const run = () => {
    const el = scopeEl();
    if (!el) return;
    const terms = norm(qRef.current).trim().split(" ").filter(Boolean);
    setHits(apply(el, terms));
  };

  useEffect(() => {
    const el = scopeEl();
    if (!el) return undefined;
    let raf = 0;
    const check = () => {
      setEnough(el.querySelectorAll(CONTROLS).length >= minControls);
      if (qRef.current.trim()) run();
    };
    check();
    const mo = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(check); });
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); cancelAnimationFrame(raf); clear(el); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { run(); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = scopeEl();
    if (open) inputRef.current?.focus();
    if (!el || inline) return undefined;
    if (open) el.setAttribute("data-ws-open", ""); else el.removeAttribute("data-ws-open");
    return () => el.removeAttribute("data-ws-open");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enough && !q) return <span ref={rootRef} data-ws-keep="" className="hidden" />;
  const close = () => { setQ(""); setOpen(false); };
  const pos = inline ? "relative" : "absolute right-11 top-3 z-[60]";
  return (
    <div ref={rootRef} data-ws-keep="" className={`${pos} ${className}`} data-testid="window-search">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="window-search-open" title="Search this window"
          className="flex h-7 w-7 items-center justify-center rounded-md text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-[#00F0FF]"><Search size={14} /></button>
      ) : (
        <div className="flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 bg-[#0d0d12] px-2 py-1 shadow-lg cm-pop-in">
          <Search size={13} className="shrink-0 text-[#00F0FF]" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this window…" data-testid="window-search-input"
            onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); } }}
            className={`${inline ? "w-24 min-w-0" : "w-40 sm:w-48"} bg-transparent text-xs text-white outline-none placeholder:text-[#666]`} />
          {q && <span data-testid="window-search-count" className={`shrink-0 text-[10px] font-semibold ${hits ? "text-[#A1A1AA]" : "text-[#f87171]"}`}>{hits ? `${hits} found` : (inline ? "None" : "No matches")}</span>}
          <button type="button" onClick={close} data-testid="window-search-clear" className="shrink-0 rounded p-0.5 text-[#A1A1AA] hover:text-white"><X size={12} /></button>
        </div>
      )}
    </div>
  );
}
